import { createElement, Fragment, type ReactElement, type ReactNode } from "react"

import { normalizeLocale } from "../config"
import type { Locale } from "../types"
import { messages } from "./messages"

type Values = Record<string, string | number | undefined>
type RichValue = string | number | ((chunks: string) => ReactNode)
type RichStringValue = string | number | ((chunks: string) => string)
type RichContent = string | ReactElement | ReactNode[]

export type Translator = {
  (key: string, values?: Values): string
  rich: {
    (key: string, values?: Record<string, RichStringValue>): string
    (key: string, values: Record<string, RichValue>): RichContent
  }
  locale: Locale
}

export const createTranslator = (localeValue: Locale): Translator => {
  const locale = normalizeLocale(localeValue)

  const translate = ((key: string, values?: Values) => {
    const template = readMessage(locale, key) ?? readMessage("en", key) ?? key
    return interpolate(template, values)
  }) as Translator

  translate.rich = ((key: string, values: Record<string, RichValue> = {}) => {
    const template = interpolate(
      readMessage(locale, key) ?? readMessage("en", key) ?? key,
      Object.fromEntries(
        Object.entries(values).filter((entry): entry is [string, string | number] => typeof entry[1] !== "function")
      )
    )
    const pattern = /<(\w+)>(.*?)<\/\1>/g
    const parts: ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(template))) {
      if (match.index > lastIndex) parts.push(template.slice(lastIndex, match.index))
      const name = match[1]
      const chunks = match[2]
      if (!name || chunks === undefined) continue
      const renderer = values[name]
      const content = typeof renderer === "function" ? renderer(chunks) : chunks
      parts.push(content)
      lastIndex = pattern.lastIndex
    }
    if (lastIndex === 0) return template
    if (lastIndex < template.length) parts.push(template.slice(lastIndex))
    if (parts.every((part) => typeof part === "string" || typeof part === "number")) return parts.join("")
    return parts.map((part, index) => createElement(Fragment, { key: index }, part))
  }) as Translator["rich"]
  translate.locale = locale

  return translate
}

const readMessage = (locale: Locale, key: string): string | undefined => {
  const parts = key.split(".")
  let current: unknown = messages[locale]
  for (const part of parts) {
    if (!current || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return typeof current === "string" ? current : undefined
}

const interpolate = (template: string, values: Values = {}): string =>
  template.replace(/\{(\w+)\}/g, (_match, key: string) => String(values[key] ?? ""))
